<script>
import { authComputed } from '@state/helpers'
import { technologyIcons } from '@utils/consts/technologyIcons'
import _ from 'lodash'

export default {
  props: {
    appDescriptions: {
      type: Array,
      default: () => [],
    },
    selectedAppNameDescription: {
      type: String,
      default: '',
    },
  },
  data() {
    return { selectedIndex: 0 }
  },
  computed: {
    ...authComputed,
    selectedAppDescriptions() {
      if (!this.appDescriptions.length) {
        return []
      }

      const [, ...descriptions] = this.appDescriptions[this.selectedIndex]
      return descriptions
    },
    selectedAppTitle() {
      if (!this.appDescriptions.length) {
        return ''
      }

      return this.appDescriptions[this.selectedIndex][0].title
    },
    selectedAppInfo() {
      if (!this.appDescriptions.length) {
        return {}
      }

      const appName = this.appDescriptions[this.selectedIndex][0].originalAppTitle
      const { appUrl } = _.find(this.$store.getters['apps/allApps'], { appName: appName })

      return { ...technologyIcons[this.appDescriptions[this.selectedIndex][0].originalAppTitle], appUrl }
    },
  },

  watch: {
    selectedAppNameDescription() {
      this.updateIndexAccordingToStore()
    },
  },
  created() {
    this.updateIndexAccordingToStore()
  },

  methods: {
    updateIndexAccordingToStore() {
      const targetIndex = this.appDescriptions.findIndex(
        (description) => description[0]?.originalAppTitle === this.selectedAppNameDescription
      )
      this.selectedIndex = targetIndex === -1 ? 0 : targetIndex
    },
    goToNext() {
      if (this.selectedIndex < this.appDescriptions.length - 1) {
        this.selectedIndex += 1
      } else {
        this.selectedIndex = 0
      }
    },

    goToPrevious() {
      if (this.selectedIndex > 0) {
        this.selectedIndex -= 1
      } else {
        this.selectedIndex = this.appDescriptions.length - 1
      }
    },

    launchApp({ appUrl }) {
      window.open(appUrl, '_blank')
    },

    redirectToMyTech() {
      this.$router.push({ path: '/my-tech', hash: 'tailoredTechnology' })
    },
  },
}
</script>

<template>
  <b-modal
    id="app-new-access-model"
    :modal-class="['right', 'modal-resource']"
    hide-footer
    hide-header
    @show="updateIndexAccordingToStore">
    <div :class="$style.container" data-modal-app-new-access>
      <div v-if="appDescriptions.length > 1" :class="$style.buttons">
        <div @click="goToPrevious">
          <MottoIcon :class="$style.previousButton" name="Arrow" icon-color="orange" class="cursor-pointer" />
          <p>Previous</p>
        </div>
        <div @click="goToNext">
          <p>Next</p>
          <MottoIcon :class="$style.nextButton" name="Arrow" icon-color="orange" class="cursor-pointer" />
        </div>
      </div>
      <div v-else style="height: 40px"></div>
      <div :class="$style.congratulationsContainer">
        <h2>Congratulations {{ currentUser.firstName }}!</h2>
        <p
          >Your new <b>{{ selectedAppTitle }}</b> access includes...</p
        >
      </div>
      <div v-for="description in selectedAppDescriptions" :key="description.title">
        <h3 :class="$style.newGroupsHeader">{{ description.groups }}</h3>
        <div :class="$style.descriptionsContainer">
          <span><b v-html="description.title + ' - '" /> {{ description.description }}</span>
        </div>
      </div>

      <div v-if="selectedAppInfo" :class="$style.coursesContainer">
        <img :class="$style.mottoLearnLogo" src="@assets/images/motto-learn-transparent.png" alt="" />
        <p class="color-primary-blue font-weight-bold"
          ><a
            :href="selectedAppInfo.mottoLearnUrl"
            target="_blank"
            class="color-secondary-light-blue"
            style="text-decoration: underline"
            >> CLICK HERE</a
          >
          for {{ selectedAppTitle }} courses tailored to your role(s).
        </p>
      </div>
      <div :class="$style.learnIcons">
        <div>
          <MottoIcon name="RocketColored"></MottoIcon>
          <p
            class="color-secondary-light-blue cursor-pointer"
            style="text-decoration: underline"
            @click="launchApp(selectedAppInfo)"
            >> Launch App</p
          ></div
        >
        <div>
          <MottoIcon :class="$style.big" name="ComputerColored"></MottoIcon>
          <p
            class="color-secondary-light-blue cursor-pointer"
            style="text-decoration: underline"
            @click="redirectToMyTech"
            >> See all of my Motto Technology access</p
          ></div
        >
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss" module>
@import '@design';

.container {
  @media (max-width: $lg-breakpoint) {
    width: 100vw;
    min-width: 100vw;
  }

  display: flex;
  flex-direction: column;
  max-width: 600px;
  min-height: 100%;
}

.previousButton {
  > svg {
    width: 30px;
    height: 30px;
  }
}

.nextButton {
  > svg {
    width: 30px;
    height: 30px;
    transform: rotate(180deg);
  }
}

.buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;

  > div {
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 5px;

    p {
      font-size: 16px;
      font-weight: bold;
      color: $secondary-orange;
    }
  }
}

.congratulationsContainer {
  @media (max-width: $lg-breakpoint) {
    width: 100vw;
    min-width: 100vw;
    margin: 0 0 0 18px;
  }

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 95%;
  padding: 20px 0;
  margin: 0 0 5px 24px;
  overflow: hidden;
  background-color: $primary-blue;
  border-radius: 8px;

  h2 {
    z-index: 1;
    font-size: 32px;
    color: $white;
    text-align: center;
  }

  p {
    z-index: 1;
    font-size: 18px;
    color: $white;
  }
}

.descriptionsContainer {
  margin: 15px 50px 0;
  color: $primary-blue;
}

.coursesContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 30px;

  p {
    font-size: 16px;
  }
}

.mottoLearnLogo {
  display: block;
  width: 200px;
}

.learnIcons {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 30px 30px;
  background: $lightest-gray;
  border-radius: 8px;

  div {
    display: flex;
    align-items: center;
    margin: 5px 15px;
  }

  svg {
    width: 30px;
    height: 30px;
  }

  .big {
    svg {
      width: 33px;
      height: 33px;
    }
  }

  p {
    font-size: 16px;
    font-weight: bold;
  }
}

.newGroupsHeader {
  margin: 15px 0;
  text-align: center;
  text-decoration: underline;
}
</style>
